/**
 * 13-Week Cash Forecast — Wave F.
 *
 * Pure derivation. Projects weekly cash position by combining:
 *   • Starting cash balance (sum of asset cash accounts as of asOf)
 *   • Open AR with aging-weighted collection probabilities
 *   • Open AP bills with their dueDate scheduled as outflows
 *   • Active recurring JE templates, their cadence projected forward
 *
 * Output is 13 weekly buckets, each with opening balance, expected inflow,
 * expected outflow, net, and closing balance. UI renders as a sparkline +
 * table; controller uses the "minimum projected balance" + week index as an
 * early-warning signal.
 */
import type { JournalEntry } from "../glTypes";
import type { Bill } from "../ap/apTypes";
import type { CustomerAR } from "../ar/arAging";
import type { RecurringTemplate } from "../recurring/RecurringJournalContext";

const dayMs = 86_400_000;
const CENT = (n: number) => Math.round(n * 100);

/** AR aging-bucket → collection probability inside the next 90 days. */
const COLLECTION_PROBABILITY: Record<string, number> = {
  current: 0.95,
  "1-30": 0.85,
  "31-60": 0.65,
  "61-90": 0.40,
  "90+": 0.15,
};

/** Mean # of weeks-from-now until an aging bucket is expected to land. */
const COLLECTION_WEEK_OFFSET: Record<string, number> = {
  current: 2,
  "1-30": 3,
  "31-60": 5,
  "61-90": 7,
  "90+": 10,
};

const CASH_ACCOUNT_IDS = new Set(["1000", "1010", "1020"]);

function startOfWeek(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  const day = d.getUTCDay(); // 0 = Sun
  const offset = day; // anchor weeks to Sunday
  const monday = new Date(d.getTime() - offset * dayMs);
  return monday.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  return new Date(d.getTime() + days * dayMs).toISOString().slice(0, 10);
}

function weekIndex(weekStart: string, targetIso: string): number {
  const ws = new Date(weekStart + "T00:00:00Z").getTime();
  const t = new Date(targetIso + "T00:00:00Z").getTime();
  return Math.floor((t - ws) / (7 * dayMs));
}

export interface CashForecastWeek {
  weekStart: string;            // yyyy-mm-dd (Sunday)
  weekLabel: string;            // "Wk 1" .. "Wk 13"
  opening: number;
  inflowExpected: number;
  inflowConfidence: number;     // 0..1, weighted avg
  outflowScheduled: number;
  outflowRecurring: number;
  net: number;
  closing: number;
}

export interface CashForecast {
  asOf: string;
  startingCash: number;
  weeks: CashForecastWeek[];
  totalExpectedInflow: number;
  totalScheduledOutflow: number;
  totalRecurringOutflow: number;
  minProjectedBalance: number;
  minProjectedWeekIndex: number; // 0..12, -1 if always positive
  hitsZero: boolean;
}

function startingCashBalance(
  journalEntries: JournalEntry[],
  asOf: string,
): number {
  let bal = 0;
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (je.postingDate > asOf) continue;
    for (const l of je.lines) {
      if (!CASH_ACCOUNT_IDS.has(l.accountId)) continue;
      bal += l.side === "debit" ? l.amount : -l.amount;
    }
  }
  return Math.round(bal * 100) / 100;
}

/** Advance a recurring template's nextRunDate by its cadence (UTC, no DST). */
function advanceCadence(iso: string, cadence: "monthly" | "quarterly" | "annually"): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (cadence === "monthly") return new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
  if (cadence === "quarterly") return new Date(Date.UTC(y, m + 2, d)).toISOString().slice(0, 10);
  return new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
}

/** Net cash impact (positive = inflow, negative = outflow) of a recurring template. */
function templateCashImpact(template: RecurringTemplate): number {
  let net = 0;
  for (const l of template.lines) {
    if (!CASH_ACCOUNT_IDS.has(l.accountId)) continue;
    net += l.side === "debit" ? l.amount : -l.amount;
  }
  return net;
}

export function buildCashForecast(input: {
  asOf: string;
  journalEntries: JournalEntry[];
  openBills: Bill[];
  customerAr: CustomerAR[];
  recurringTemplates: RecurringTemplate[];
}): CashForecast {
  const asOf = input.asOf;
  const startingCash = startingCashBalance(input.journalEntries, asOf);
  const w0 = startOfWeek(asOf);

  const weeks: CashForecastWeek[] = Array.from({ length: 13 }, (_, i) => ({
    weekStart: addDays(w0, i * 7),
    weekLabel: `Wk ${i + 1}`,
    opening: 0,
    inflowExpected: 0,
    inflowConfidence: 0,
    outflowScheduled: 0,
    outflowRecurring: 0,
    net: 0,
    closing: 0,
  }));

  // (a) AR: probability-weighted inflow per customer, slotted by aging bucket.
  let totalInflow = 0;
  let weightedConfidenceNumer = 0;
  let weightedConfidenceDenom = 0;
  for (const cust of input.customerAr) {
    for (const inv of cust.openInvoices) {
      const prob = COLLECTION_PROBABILITY[inv.bucket] ?? 0.5;
      const wkOffset = COLLECTION_WEEK_OFFSET[inv.bucket] ?? 4;
      const idx = Math.max(0, Math.min(12, wkOffset - 1));
      const expected = inv.openAmount * prob;
      weeks[idx].inflowExpected += expected;
      weightedConfidenceNumer += prob * inv.openAmount;
      weightedConfidenceDenom += inv.openAmount;
      totalInflow += expected;
    }
    if (cust.unappliedCredit > 0) {
      // Unapplied credit = cash already in the door, no future inflow.
    }
  }

  // (b) AP: scheduled outflows by dueDate, full bill amount (open portion).
  let totalScheduled = 0;
  for (const bill of input.openBills) {
    if (bill.status === "paid" || bill.status === "void") continue;
    const open = bill.total - bill.amountPaid;
    if (open <= 0) continue;
    const ws = startOfWeek(bill.dueDate);
    const idx = weekIndex(w0, ws);
    if (idx < 0) {
      weeks[0].outflowScheduled += open; // overdue → next week
      totalScheduled += open;
    } else if (idx <= 12) {
      weeks[idx].outflowScheduled += open;
      totalScheduled += open;
    }
  }

  // (c) Recurring templates: project nextRunDate forward through the horizon.
  let totalRecurring = 0;
  const horizonEnd = addDays(w0, 13 * 7 - 1);
  for (const tpl of input.recurringTemplates) {
    if (!tpl.active) continue;
    const impact = templateCashImpact(tpl);
    if (impact === 0) continue;
    let runDate = tpl.nextRunDate;
    let safety = 0;
    while (runDate <= horizonEnd && safety++ < 30) {
      if (tpl.endDate && runDate > tpl.endDate) break;
      const idx = weekIndex(w0, startOfWeek(runDate));
      if (idx >= 0 && idx <= 12) {
        if (impact > 0) weeks[idx].inflowExpected += impact;
        else weeks[idx].outflowRecurring += Math.abs(impact);
        totalRecurring += Math.abs(impact);
      }
      runDate = advanceCadence(runDate, tpl.cadence);
    }
  }

  // Roll opening → closing across weeks.
  let runningBalance = startingCash;
  let minBal = startingCash;
  let minIdx = -1;
  for (let i = 0; i < weeks.length; i++) {
    const w = weeks[i];
    w.opening = Math.round(runningBalance * 100) / 100;
    w.net = Math.round((w.inflowExpected - w.outflowScheduled - w.outflowRecurring) * 100) / 100;
    runningBalance = w.opening + w.net;
    w.closing = Math.round(runningBalance * 100) / 100;
    w.inflowConfidence = weightedConfidenceDenom > 0
      ? weightedConfidenceNumer / weightedConfidenceDenom
      : 0;
    if (CENT(w.closing) < CENT(minBal)) { minBal = w.closing; minIdx = i; }
  }

  return {
    asOf,
    startingCash,
    weeks,
    totalExpectedInflow: Math.round(totalInflow * 100) / 100,
    totalScheduledOutflow: Math.round(totalScheduled * 100) / 100,
    totalRecurringOutflow: Math.round(totalRecurring * 100) / 100,
    minProjectedBalance: minBal,
    minProjectedWeekIndex: minIdx,
    hitsZero: minBal < 0,
  };
}
